/**
 * A/B Testing Hook
 *
 * Assigns users to experiment variants and persists the assignment.
 * Uses localStorage for persistence and tracks variant in GA4.
 */
import { useState, useEffect } from 'react';

// Experiment configuration
export const EXPERIMENTS = {
  LANDING_PAGE_HERO: {
    // v3 : nouvel id pour un split propre (les anciens bucketés v2 ne polluent pas).
    id: 'landing_hero_v3',
    // Historique : anciennes B (preuve sociale) et C (faux chatbot) retirées le
    // 2026-08-11 (sous-performaient A sur form_started). Nouveau challenger B
    // = offre « courrier de demande de pièces prêt à poster » (livrable payant) :
    // hero + paywall recentrés sur le courrier, livrable post-paiement mis en avant.
    // A reste le contrôle (pitch « analyse »).
    variants: ['A', 'B'],
    // Traffic allocation: percentage for each variant (must sum to 100)
    weights: [50, 50],
  },
};

/**
 * Check URL for forced variant (e.g., ?variant=B)
 * @param {string[]} variants - Valid variant names
 * @returns {string|null} Forced variant or null
 */
const getURLVariant = (variants) => {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  const forcedVariant = urlParams.get('variant');

  if (forcedVariant && variants.includes(forcedVariant.toUpperCase())) {
    return forcedVariant.toUpperCase();
  }
  return null;
};

/**
 * Get or assign a variant for an experiment
 * @param {string} experimentId - Unique experiment identifier
 * @param {string[]} variants - Array of variant names
 * @param {number[]} weights - Percentage weights for each variant
 * @returns {string} The assigned variant
 */
const getOrAssignVariant = (experimentId, variants, weights) => {
  const storageKey = `ab_test_${experimentId}`;

  // Priority 1: Check URL parameter (for testing)
  const urlVariant = getURLVariant(variants);
  if (urlVariant) {
    return urlVariant;
  }

  // Priority 2: Check for existing assignment
  const existingVariant = localStorage.getItem(storageKey);
  if (existingVariant && variants.includes(existingVariant)) {
    return existingVariant;
  }

  // Assign new variant based on weights
  const random = Math.random() * 100;
  let cumulative = 0;
  let assignedVariant = variants[0];

  for (let i = 0; i < variants.length; i++) {
    cumulative += weights[i];
    if (random < cumulative) {
      assignedVariant = variants[i];
      break;
    }
  }

  // Persist assignment
  localStorage.setItem(storageKey, assignedVariant);

  return assignedVariant;
};

/**
 * Track variant assignment in GA4
 * @param {string} experimentId - Experiment ID
 * @param {string} variant - Assigned variant
 */
const trackVariantAssignment = (experimentId, variant) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // Set user property for the experiment
    window.gtag('set', 'user_properties', {
      [`experiment_${experimentId}`]: variant,
    });

    // Send experiment assignment event
    window.gtag('event', 'experiment_impression', {
      experiment_id: experimentId,
      variant_id: variant,
      event_category: 'Experiment',
      event_label: `${experimentId}:${variant}`,
    });
  }
};

/**
 * Hook to get the assigned variant for an experiment
 * @param {Object} experiment - Experiment configuration from EXPERIMENTS
 * @returns {{ variant: string, isLoading: boolean }}
 */
export const useABTest = (experiment) => {
  // Assign variant synchronously — no loading state, no spinner
  const [variant] = useState(() =>
    getOrAssignVariant(experiment.id, experiment.variants, experiment.weights)
  );

  // Track in GA4 after mount (non-blocking)
  useEffect(() => {
    trackVariantAssignment(experiment.id, variant);
  }, [experiment.id, variant]);

  return { variant, isLoading: false };
};

/**
 * Force a specific variant (for testing/debugging)
 * @param {string} experimentId - Experiment ID
 * @param {string} variant - Variant to force
 */
export const forceVariant = (experimentId, variant) => {
  const storageKey = `ab_test_${experimentId}`;
  localStorage.setItem(storageKey, variant);
  window.location.reload();
};

/**
 * Clear experiment assignment (for testing)
 * @param {string} experimentId - Experiment ID
 */
export const clearExperiment = (experimentId) => {
  const storageKey = `ab_test_${experimentId}`;
  localStorage.removeItem(storageKey);
};

/**
 * Get current variant without re-assignment (for analytics)
 * @param {string} experimentId - Experiment ID
 * @returns {string|null}
 */
export const getCurrentVariant = (experimentId) => {
  const storageKey = `ab_test_${experimentId}`;
  return localStorage.getItem(storageKey);
};

export default useABTest;
